import React, { useState, useEffect } from 'react'

// Generer indeværende år + 3 år frem (f.eks. 2026/2027, 2027/2028, 2028/2029, 2029/2030)
const getSchoolYears = () => {
  const currentYear = new Date().getFullYear()
  return [0, 1, 2, 3].map((offset) => {
    const start = currentYear + offset
    return `${start}/${start + 1}`
  })
}

export default function BookingForm({ initialDate = null }) {
  const schoolYears = getSchoolYears()
  const [datesData, setDatesData] = useState([])
  const [loadingSlots, setLoadingSlots] = useState(true)
  const [slotsError, setSlotsError] = useState(null)

  const [selectedDate, setSelectedDate] = useState(initialDate)
  const [selectedTime, setSelectedTime] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    participants: 2,
    schoolYear: schoolYears[0] || '2026/2027',
    grade: '9. klasse',
    message: '',
    website_url: '' // Anti-spam honeypot
  })

  const [fieldErrors, setFieldErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [serverError, setServerError] = useState(null)
  const [confirmation, setConfirmation] = useState(null)

  // Fetch available slots from backend serverless function
  const fetchSlots = async () => {
    setLoadingSlots(true)
    setSlotsError(null)
    try {
      const res = await fetch('/_fn/bookings-get-slots?days=90')
      if (!res.ok) throw new Error('Kunne ikke hente ledige tider')
      const data = await res.json()
      if (data.success && Array.isArray(data.dates)) {
        setDatesData(data.dates)
        // Auto-select first date with available slots if none selected
        if (!selectedDate) {
          const firstWithSlots = data.dates.find((d) => d.availableSlots && d.availableSlots.length > 0)
          if (firstWithSlots) {
            setSelectedDate(firstWithSlots.date)
            setSelectedTime(firstWithSlots.availableSlots[0])
          }
        }
      }
    } catch (err) {
      setSlotsError('Der opstod en midlertidig fejl ved indlæsning af ledige tider. Prøv venligst igen.')
    } finally {
      setLoadingSlots(false)
    }
  }

  useEffect(() => {
    fetchSlots()
  }, [])

  const selectedDateObj = datesData.find((d) => d.date === selectedDate)

  const validateForm = () => {
    const errors = {}
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      errors.name = 'Indtast venligst dit fulde navn (forælder eller elev).'
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Indtast en gyldig e-mailadresse.'
    }
    if (!formData.phone.trim() || formData.phone.trim().replace(/\s+/g, '').length < 8) {
      errors.phone = 'Indtast et gyldigt telefonnummer (mindst 8 cifre).'
    }
    if (!selectedDate) {
      errors.date = 'Vælg venligst en dato.'
    }
    if (!selectedTime) {
      errors.time = 'Vælg venligst et tidspunkt.'
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setServerError(null)

    if (!validateForm()) return

    setIsSubmitting(true)

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        participants: parseInt(formData.participants, 10),
        grade: `${formData.grade} (${formData.schoolYear})`,
        school_year: formData.schoolYear,
        message: formData.message.trim(),
        slot_date: selectedDate,
        slot_time: selectedTime,
        website_url: formData.website_url // Honeypot
      }

      const res = await fetch('/_fn/bookings-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      const data = await res.json()

      if (res.status === 409) {
        // Double booking conflict handled at DB level
        setServerError(data.error || 'Dette tidspunkt er netop blevet reserveret af en anden. Vælg venligst et andet tidspunkt.')
        // Refresh slots to remove booked time
        fetchSlots()
        setSelectedTime('')
        setIsSubmitting(false)
        return
      }

      if (!res.ok || !data.success) {
        if (data.errors) {
          setFieldErrors(data.errors)
        } else {
          setServerError(data.error || 'Der opstod en fejl under bookingen. Kontroller venligst dine oplysninger.')
        }
        setIsSubmitting(false)
        return
      }

      // Success
      setConfirmation(data)
      setIsSubmitting(false)
    } catch (err) {
      setServerError('Kunne ikke oprette forbindelse til serveren. Kontroller din internetforbindelse og prøv igen.')
      setIsSubmitting(false)
    }
  }

  // Confirmation view
  if (confirmation) {
    const details = confirmation.details || {}
    return (
      <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-xl border border-stone-200/80 max-w-2xl mx-auto text-left">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-6">
          <i className="fa-solid fa-circle-check text-3xl" aria-hidden="true" />
        </div>

        <h3 className="text-2xl sm:text-3xl font-extrabold text-[#122020] mb-2">
          Tak for din booking!
        </h3>
        <p className="text-stone-600 mb-8 text-base">
          Vi glæder os meget til at byde jer velkommen på Emmerske Efterskole. Vi har sendt en bekræftelse til <strong className="text-stone-900">{details.email}</strong>.
        </p>

        <div className="bg-[#f7faf9] rounded-2xl p-6 border border-[#007a53]/20 space-y-4 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#007a53]/15 text-[#007a53] flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-calendar-day" aria-hidden="true" />
            </div>
            <div>
              <div className="text-xs text-stone-500 uppercase font-semibold">Tid & Dato</div>
              <div className="text-lg font-bold text-[#122020]">
                {details.date} kl. {details.time}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#007a53]/15 text-[#007a53] flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-location-dot" aria-hidden="true" />
            </div>
            <div>
              <div className="text-xs text-stone-500 uppercase font-semibold">Mødested</div>
              <div className="text-base font-semibold text-[#122020]">
                Emmerske Efterskole, Åbenråvej 14, 6270 Tønder
              </div>
              <p className="text-xs text-stone-500 mt-0.5">
                Henvend jer ved hovedbygningen / kontoret ved ankomst.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#007a53]/15 text-[#007a53] flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-user-group" aria-hidden="true" />
            </div>
            <div>
              <div className="text-xs text-stone-500 uppercase font-semibold">Deltagere & Klasse</div>
              <div className="text-sm font-medium text-stone-700">
                {details.name} ({details.participants} personer) · {details.grade || 'Ikke angivet'}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <a
            href="https://maps.google.com/?q=%C3%85benr%C3%A5vej+14,+6270+T%C3%B8nder"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary w-full sm:w-auto text-center"
          >
            <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
            <span>Få rutevejledning</span>
          </a>

          <button
            type="button"
            onClick={() => {
              setConfirmation(null)
              fetchSlots()
            }}
            className="text-stone-600 hover:text-[#007a53] text-sm font-semibold underline underline-offset-4 py-2 px-4"
          >
            Book en anden tid
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-xl border border-stone-200/80 w-full">
      <form onSubmit={handleSubmit} noValidate>
        {/* Anti-spam honeypot - strictly hidden from humans */}
        <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
          <label htmlFor="booking_website_url">Hjemmeside (udfyld ikke)</label>
          <input
            id="booking_website_url"
            type="text"
            name="website_url"
            tabIndex="-1"
            value={formData.website_url}
            onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
            autoComplete="off"
          />
        </div>

        {serverError && (
          <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-3">
            <i className="fa-solid fa-triangle-exclamation text-amber-600 mt-0.5 text-base flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">{serverError}</p>
            </div>
          </div>
        )}

        {/* Trin 1: Vælg dato & tidspunkt */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-7 h-7 rounded-full bg-[#007a53] text-white text-xs font-bold flex items-center justify-center">
              1
            </span>
            <h4 className="text-lg font-bold text-[#122020]">
              Vælg ledig dato og tidspunkt
            </h4>
          </div>

          {loadingSlots ? (
            <div className="py-12 text-center text-stone-500">
              <i className="fa-solid fa-circle-notch fa-spin text-2xl text-[#007a53] mb-3" aria-hidden="true" />
              <p className="text-sm">Henter ledige rundvisningstider fra systemet...</p>
            </div>
          ) : slotsError ? (
            <div className="p-4 rounded-xl bg-red-50 text-red-700 text-sm flex items-center justify-between">
              <span>{slotsError}</span>
              <button
                type="button"
                onClick={fetchSlots}
                className="text-xs font-bold underline ml-3"
              >
                Prøv igen
              </button>
            </div>
          ) : datesData.length === 0 ? (
            <div className="p-8 text-center rounded-2xl bg-stone-50 border border-dashed border-stone-300">
              <div className="w-12 h-12 rounded-full bg-[#007a53]/10 text-[#007a53] flex items-center justify-center mx-auto mb-3">
                <i className="fa-solid fa-calendar-xmark text-xl" aria-hidden="true" />
              </div>
              <h5 className="font-bold text-stone-800 text-base mb-1">
                Der er p.t. ingen åbne rundvisningsdatoer i systemet
              </h5>
              <p className="text-xs text-stone-600 max-w-md mx-auto mb-4">
                Skolen opdaterer løbende kalenderen. Kontakt os gerne direkte på tlf. <span className="whitespace-nowrap font-semibold">74 72 44 33</span> for at aftale et personligt besøg.
              </p>
              <a
                href="tel:74724433"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#007a53] text-white text-xs font-bold hover:bg-[#006343] transition-colors"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Ring på 74 72 44 33</span>
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Horisontal scroller til datoer */}
              <div className="overflow-x-auto pb-2 pt-1 -mx-2 px-2 scrollbar-thin">
                <div className="flex items-stretch gap-2.5 min-w-max">
                  {datesData.map((d) => {
                    const isSelected = selectedDate === d.date
                    const hasSlots = d.availableSlots && d.availableSlots.length > 0
                    const monthNamesShort = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']
                    const parts = d.date.split('-')
                    const monthIdx = parseInt(parts[1], 10) - 1
                    const monthStr = monthNamesShort[monthIdx] || ''
                    const dayNum = parseInt(parts[2], 10)

                    return (
                      <button
                        key={d.date}
                        type="button"
                        disabled={!hasSlots}
                        onClick={() => {
                          setSelectedDate(d.date)
                          if (hasSlots) {
                            setSelectedTime(d.availableSlots[0])
                          } else {
                            setSelectedTime('')
                          }
                        }}
                        className={`text-left p-3 rounded-2xl transition-all flex flex-col justify-between w-32 ${
                          isSelected
                            ? 'bg-[#007a53] text-white shadow-md shadow-[#007a53]/20 border-0'
                            : hasSlots
                            ? 'bg-white hover:bg-stone-50 text-stone-800 border border-stone-200 shadow-sm'
                            : 'bg-stone-100 text-stone-400 border border-stone-100 cursor-not-allowed opacity-60'
                        }`}
                      >
                        <div className={`text-[11px] font-bold uppercase tracking-wider ${isSelected ? 'text-white/80' : 'text-stone-500'}`}>
                          {d.dayOfWeek}
                        </div>
                        <div className="text-base font-extrabold my-1">
                          {dayNum}. {monthStr}
                        </div>
                        <div className={`text-[11px] font-medium ${isSelected ? 'text-white/90' : hasSlots ? 'text-black font-semibold' : 'text-stone-400'}`}>
                          {hasSlots ? `${d.availableSlots.length} ledige` : 'Optaget'}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Tidsvalg for den valgte dato */}
              {selectedDateObj && (
                <div className="mt-4 pt-1">
                  <div className="text-xs font-semibold text-stone-600 mb-2.5">
                    Ledige tidspunkter for {selectedDateObj.dayName}:
                  </div>
                  {selectedDateObj.availableSlots && selectedDateObj.availableSlots.length > 0 ? (
                    <div className="flex flex-wrap gap-2.5">
                      {selectedDateObj.availableSlots.map((time) => {
                        const isTimeSelected = selectedTime === time
                        return (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setSelectedTime(time)}
                            className={`px-4 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 cursor-pointer ${
                              isTimeSelected
                                ? 'bg-[#007a53] text-white shadow-sm ring-2 ring-[#007a53] ring-offset-2'
                                : 'bg-transparent text-stone-800 hover:text-black hover:bg-stone-100'
                            }`}
                          >
                            <i className="fa-regular fa-clock text-xs" aria-hidden="true" />
                            <span>Kl. {time}</span>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-stone-500">
                      Der er ingen ledige tider på denne dag. Vælg venligst en anden dag ovenfor.
                    </p>
                  )}
                </div>
              )}

              {fieldErrors.date && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.date}</p>
              )}
              {fieldErrors.time && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.time}</p>
              )}
            </div>
          )}
        </div>

        {/* Trin 2: Kontaktoplysninger */}
        <div className="mb-8 pt-6 border-t border-stone-200">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-7 h-7 rounded-full bg-[#007a53] text-white text-xs font-bold flex items-center justify-center">
              2
            </span>
            <h4 className="text-lg font-bold text-[#122020]">
              Dine kontaktoplysninger
            </h4>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Navn */}
            <div className="sm:col-span-2">
              <label htmlFor="booking_name" className="block text-xs font-semibold text-stone-700 mb-1">
                Navn (forælder / elev) <span className="text-red-500">*</span>
              </label>
              <input
                id="booking_name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value })
                  if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: null })
                }}
                placeholder="F.eks. Mette Hansen"
                className={`w-full px-4 py-3 rounded-xl border bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53] ${
                  fieldErrors.name ? 'border-red-400' : 'border-stone-200'
                }`}
              />
              {fieldErrors.name && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="booking_email" className="block text-xs font-semibold text-stone-700 mb-1">
                E-mailadresse <span className="text-red-500">*</span>
              </label>
              <input
                id="booking_email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value })
                  if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: null })
                }}
                placeholder="din@email.dk"
                className={`w-full px-4 py-3 rounded-xl border bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53] ${
                  fieldErrors.email ? 'border-red-400' : 'border-stone-200'
                }`}
              />
              {fieldErrors.email && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.email}</p>
              )}
            </div>

            {/* Telefon */}
            <div>
              <label htmlFor="booking_phone" className="block text-xs font-semibold text-stone-700 mb-1">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                id="booking_phone"
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => {
                  setFormData({ ...formData, phone: e.target.value })
                  if (fieldErrors.phone) setFieldErrors({ ...fieldErrors, phone: null })
                }}
                placeholder="12 34 56 78"
                className={`w-full px-4 py-3 rounded-xl border bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53] ${
                  fieldErrors.phone ? 'border-red-400' : 'border-stone-200'
                }`}
              />
              {fieldErrors.phone && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.phone}</p>
              )}
            </div>

            {/* Antal deltagere */}
            <div>
              <label htmlFor="booking_participants" className="block text-xs font-semibold text-stone-700 mb-1">
                Antal deltagere
              </label>
              <select
                id="booking_participants"
                value={formData.participants}
                onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53]"
              >
                <option value="1">1 person</option>
                <option value="2">2 personer (elev + forælder)</option>
                <option value="3">3 personer</option>
                <option value="4">4 eller flere</option>
              </select>
            </div>

            {/* Interesseret i at starte i skoleåret... */}
            <div>
              <label htmlFor="booking_school_year" className="block text-xs font-semibold text-stone-700 mb-1">
                Interesseret i at starte i skoleåret... <span className="text-red-500">*</span>
              </label>
              <select
                id="booking_school_year"
                value={formData.schoolYear}
                onChange={(e) => setFormData({ ...formData, schoolYear: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53]"
              >
                {schoolYears.map((yearOption) => (
                  <option key={yearOption} value={yearOption}>
                    {yearOption}
                  </option>
                ))}
              </select>
            </div>

            {/* Ønsket klassetrin */}
            <div className="sm:col-span-2">
              <label htmlFor="booking_grade" className="block text-xs font-semibold text-stone-700 mb-1">
                Interesseret i klassetrin
              </label>
              <select
                id="booking_grade"
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53]"
              >
                <option value="8. klasse">8. klasse</option>
                <option value="9. klasse">9. klasse</option>
                <option value="10. klasse">10. klasse</option>
                <option value="Flere årgange / uafklaret">Uafklaret endnu</option>
              </select>
            </div>

            {/* Beskrivelse / bemærkninger */}
            <div className="sm:col-span-2">
              <label htmlFor="booking_message" className="block text-xs font-semibold text-stone-700 mb-1">
                Beskrivelse / særlige ønsker
              </label>
              <textarea
                id="booking_message"
                rows="3"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Fortæl os gerne om elevens interesser (f.eks. outdoor, krea, e-sport) eller eventuelle særlige spørgsmål til ordblindestøtten."
                className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#007a53]"
              />
            </div>
          </div>
        </div>

        {/* Submit Knap */}
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-stone-500">
            {selectedDate && selectedTime ? (
              <span className="text-[#007a53] font-medium">
                Valgt: {selectedDateObj?.dayName || selectedDate} kl. {selectedTime}
              </span>
            ) : (
              <span>Vælg dato og tidspunkt ovenfor</span>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !selectedTime}
            className="btn-primary w-full sm:w-auto !py-3.5 !px-8 text-base disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin mr-2" aria-hidden="true" />
                <span>Bekræfter reservation...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-calendar-check mr-2" aria-hidden="true" />
                <span>Bekræft rundvisning</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
